// Inline playback for YouTube Shorts inside the carousel.
//
// Uses the official YouTube IFrame Player API (one YT.Player per short, created
// lazily on first play).
//
// Behaviour:
// - Click any short to play it; clicking again pauses it.
// - Playing a short pauses the others, so only one plays at a time.
// - YouTube chrome is minimised (no controls, branding, related videos, etc.).
//
// The carousel uses a plain slide effect (not the 3D coverflow), so pointer
// events reach the slides normally. A full-cover click layer (`.short-video__
// toggle`) sits above the iframe so clicks keep reaching us once a video plays.
// `is-playing` is our source of truth for intent; a player's `onReady`
// reconciles to it once the player's methods are available.

const PLAYER_VARS = {
    'controls': 0,
    'disablekb': 1,
    'fs': 0,
    'iv_load_policy': 3,
    'modestbranding': 1,
    'playsinline': 1,
    'rel': 0,
};

const carousels = document.querySelectorAll('.carousel--portrait');

let ytReady = !!(window.YT && window.YT.Player);
const readyQueue = [];

if (carousels.length) {
    ensureApi();
    carousels.forEach(setup);
}

function whenReady(callback) {
    if (ytReady) {
        callback();
    } else {
        readyQueue.push(callback);
    }
}

function ensureApi() {
    if (ytReady) {
        return;
    }

    const url = 'https://www.youtube.com/iframe_api';
    const scripts = document.getElementsByTagName('script');
    let addScript = true;

    for (let i = scripts.length; i--;) {
        if (scripts[i].src === url) {
            addScript = false;
        }
    }

    if (addScript) {
        const tag = document.createElement('script');

        tag.src = url;

        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Chain onto any existing ready handler (e.g. from the video block).
    const previousReady = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
        if (typeof previousReady === 'function') {
            previousReady();
        }
        ytReady = true;
        while (readyQueue.length) {
            readyQueue.shift()();
        }
    };
}

function setup(carousel) {
    carousel.addEventListener('click', (event) => onTap(carousel, event));
}

function onTap(carousel, event) {
    // Let the navigation arrows do their job.
    if (event.target.closest('.swiper-button-next, .swiper-button-prev')) {
        return;
    }

    const short = event.target.closest('.short-video');

    if (!short) {
        return;
    }

    if (short.classList.contains('is-playing')) {
        // Pause this one.
        short.classList.remove('is-playing');
        sync(short);
        return;
    }

    // Pause any other playing short — only one plays at a time.
    carousel.querySelectorAll('.short-video.is-playing').forEach((other) => {
        other.classList.remove('is-playing');
        sync(other);
    });

    // Play this one.
    short.classList.add('has-started', 'is-playing');

    if (short.shortPlayer) {
        sync(short);
    } else {
        whenReady(() => createPlayer(short));
    }
}

function createPlayer(short) {
    if (short.shortPlayer) {
        return;
    }

    const videoId = short.getAttribute('data-video-id');
    const playerElement = short.querySelector('.short-video__player');

    if (!videoId || !playerElement) {
        return;
    }

    short.shortPlayer = new window.YT.Player(playerElement, {
        height: '100%',
        width: '100%',
        videoId,
        playerVars: PLAYER_VARS,
        events: {
            'onReady': () => {
                short.dataset.shortReady = '1';
                sync(short);
            },
            'onStateChange': (event) => {
                const state = window.YT.PlayerState;

                if (event.data === state.PLAYING) {
                    short.classList.add('is-playing');
                } else if (event.data === state.PAUSED || event.data === state.ENDED) {
                    short.classList.remove('is-playing');
                }
            },
        },
    });
}

// Make the player match our intent (the is-playing class). No-op until the
// player has fired onReady (its methods don't exist before that).
function sync(short) {
    const player = short.shortPlayer;

    if (!player || short.dataset.shortReady !== '1' || typeof player.playVideo !== 'function') {
        return;
    }

    if (short.classList.contains('is-playing')) {
        player.playVideo();
    } else {
        player.pauseVideo();
    }
}
